import { forwardRef, useEffect, useState } from 'react';
import {
	Button,
	Form,
	Input,
	Space,
	FormInstance,
	Typography,
	Tooltip,
	notification,
	Radio,
	InputNumber,
} from 'antd';
import { QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons';
import TextEditor from '../../../../v1/components/TextEditor/TextEditor';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { updateCampaignVariables } from '@/api/novi/CamapaignPortalApis';
import { useMutation } from '@tanstack/react-query';
import { getSelectedAgent } from '@/store/account/selector';
import { safeJSONParse } from '@/utils/helper';

const { Title } = Typography;

interface TextCampaignFormProps {
	initialValues?: any;
	onSubmit?: (values: any) => void;
	onEdit?: (values: any) => void;
	onCancel?: () => void;
	isLoading: boolean;
	form?: FormInstance;
	mode?: 'create' | 'edit';
	showCancelBtn?: boolean;
}

const TextCampaignForm = forwardRef<FormInstance, TextCampaignFormProps>(
	({ onSubmit, onEdit, isLoading, form: parentForm, initialValues, mode = 'create' }, ref) => {
		const [form] = Form.useForm();
		const formInstance = parentForm || form;
		const selectedCampaign = useAppSelector(getSelectedAgent);
		const [variables, setVariables] = useState<any[]>(
			safeJSONParse(selectedCampaign?.variables, []),
		);
		const [triggerType, setTriggerType] = useState<string>(
			initialValues?.schedule?.type || 'immediate',
		);

		useEffect(() => {
			if (initialValues && Object.keys(initialValues).length > 0) {
				formInstance.resetFields();
				formInstance.setFieldsValue({
					name: initialValues.name,
					description: initialValues.description,
					message: initialValues.message,
					schedule: {
						type: initialValues.schedule?.type || 'immediate',
						delay: initialValues.schedule?.delay || undefined,
					},
				});

				// Update trigger type state
				setTriggerType(initialValues.schedule?.type || 'immediate');
			}
		}, [initialValues, formInstance]);

		const { mutate: updateVariables } = useMutation({
			mutationKey: ['update-variables'],
			mutationFn: () =>
				updateCampaignVariables(selectedCampaign?.number || '', variables, 'Client'),
			onSuccess: () => {
				notification.success({
					key: 'variables-success',
					message: 'Message variables updated successfully',
					duration: 3,
				});
			},
		});

		const handleVariableChange = (variables: any) => {
			setVariables(variables);
			updateVariables();
		};

		const handleSubmit = (values: any) => {
			const formattedValues = {
				...values,
				schedule: {
					type: triggerType,
					delay: triggerType === 'delayed' ? values?.delay : undefined,
				},
			};

			if (mode === 'edit' && onEdit) {
				onEdit(formattedValues);
			} else if (onSubmit) {
				onSubmit(formattedValues);
			}
		};

		const handleReset = () => {
			formInstance.resetFields();
			if (initialValues) {
				formInstance.setFieldsValue(initialValues);
			}
		};

		return (
			<>
				<Title level={4} className="!mb-0">
					Text Configuration
				</Title>
				<p className="text-gray-400 text-sm my-0 mt-1">
					Customize the message content, scheduling options, and personalization variables to tailor
					your campaign to your audience.
				</p>

				<Form
					ref={ref}
					form={formInstance}
					layout="vertical"
					onFinish={handleSubmit}
					className="space-y-6 mt-5"
					initialValues={{
						schedule: { type: 'immediate' },
						...initialValues,
					}}
				>
					<Form.Item
						name="name"
						label="Text Schedule Name"
						rules={[{ required: true, message: 'Please enter campaign name' }]}
					>
						<Input
							placeholder="Enter a descriptive name for your campaign"
							maxLength={100}
							showCount
						/>
					</Form.Item>

					<Form.Item
						name="description"
						label="Description"
						rules={[{ required: true, message: 'Please enter campaign description' }]}
					>
						<Input.TextArea
							placeholder="What is the purpose of this campaign?"
							rows={3}
							maxLength={500}
							showCount
						/>
					</Form.Item>

					<Form.Item
						name="message"
						label={
							<Space>
								<span>Message Template</span>
								<Tooltip title="Use variables like {{name}} for personalization">
									<QuestionCircleOutlined className="text-gray-400" />
								</Tooltip>
							</Space>
						}
						rules={[{ required: true, message: 'Please enter a message' }]}
					>
						<TextEditor
							variables={variables}
							// @ts-expect-error ignore for now
							fromPortal={'NOVI'}
							initialValue={initialValues?.message}
							onChange={(_: any, variables: any) => {
								if (variables) {
									handleVariableChange(variables);
								}
							}}
						/>
					</Form.Item>

					<Form.Item
						name={['type']}
						label="When should this campaign be triggered?"
						rules={[{ required: true, message: 'Please select when to send messages' }]}
					>
						<Radio.Group
							onChange={(e) => setTriggerType(e.target.value)}
							value={triggerType}
							defaultValue={initialValues?.schedule?.type}
						>
							<Radio value="immediate">Immediately</Radio>
							<Radio value="delayed">After a specific delay</Radio>
						</Radio.Group>
					</Form.Item>

					{triggerType === 'delayed' && (
						<Form.Item
							name={['delay', 'value']}
							label={
								<Space>
									<span>Delay Duration</span>
									<Tooltip title="How long to wait before sending messages">
										<QuestionCircleOutlined className="text-gray-400" />
									</Tooltip>
								</Space>
							}
							rules={[{ required: true, message: 'Please specify delay duration' }]}
							className="mb-5"
						>
							<InputNumber
								style={{ width: '100%' }}
								min={1}
								defaultValue={initialValues?.schedule?.delay?.value}
								placeholder="Enter number of hours"
								addonAfter="hours"
							/>
						</Form.Item>
					)}

					<div className="flex justify-center gap-3 mt-5 flex-wrap">
						<Button onClick={handleReset}>Reset Form</Button>
						<Button type="primary" htmlType="submit" loading={isLoading} icon={<SaveOutlined />}>
							{mode === 'edit' ? 'Update Configuration' : 'Create Configuration'}
						</Button>
					</div>
				</Form>
			</>
		);
	},
);

TextCampaignForm.displayName = 'TextCampaignForm';

export default TextCampaignForm;
